import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Stack, Text, Title, Table, Badge, Group } from '@mantine/core';
import { StaleDevice } from '../../data/PingSummary';

type Props = {
    data: StaleDevice[] | undefined;
};

const formatLastSeen = (iso: string | null): string => {
    if (iso === null) return 'never';
    const diffMs = Math.abs(Date.now() - new Date(iso).getTime());
    const mins = Math.round(diffMs / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `~${mins} ${mins === 1 ? 'minute' : 'minutes'} ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `~${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    const days = Math.round(hours / 24);
    if (days < 7) return `~${days} ${days === 1 ? 'day' : 'days'} ago`;
    const weeks = Math.round(days / 7);
    return `~${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
};

const StaleDevicesList: React.FC<Props> = ({ data }) => {
    const navigate = useNavigate();
    const devices = data ?? [];

    return (
        <Card shadow="xs" withBorder padding="md" h="100%">
            <Stack gap="xs" h="100%">
                <Group justify="space-between" align="baseline">
                    <Title order={5}>Stale devices</Title>
                    <Text size="xs" c="dimmed">
                        No ping in 30+ minutes
                    </Text>
                </Group>
                {devices.length === 0 ? (
                    <Text c="dimmed" size="sm">
                        All devices are reporting in. 🎉
                    </Text>
                ) : (
                    <Table verticalSpacing="xs" highlightOnHover>
                        <Table.Tbody>
                            {devices.map((d) => (
                                <Table.Tr
                                    key={d.id}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/device/${d.id}`)}
                                >
                                    <Table.Td>
                                        <Text size="sm" fw={500}>
                                            {d.name}
                                        </Text>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'right' }}>
                                        {d.lastPingAt === null ? (
                                            <Badge color="gray" variant="light" size="sm">
                                                never
                                            </Badge>
                                        ) : (
                                            <Text size="sm" c="dimmed">
                                                {formatLastSeen(d.lastPingAt)}
                                            </Text>
                                        )}
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )}
            </Stack>
        </Card>
    );
};

export default StaleDevicesList;
